import { useEffect, useState } from "react";
import { echotwinApi } from "@/lib/echotwin-api";

/**
 * EchoTwin 공개 매물 전체 검색(GET /api/v1/public/cases) — kangstudy_auction_items
 * 큐레이션 60건 밖의 매물도 지역·유형 조건으로 직접 받는다(2026-08-26 오너 추가).
 *
 * "전문가 PICK"(큐레이션 60건)과는 다른 데이터로, 이 검색 결과는 필드가 더
 * 적다(사건번호·법원·유찰횟수·강스터디 등급 없음) — 없는 값은 지어내지 않고
 * 그대로 비워서 보여준다.
 */
export interface PublicCaseCard {
  id: string;
  area: string;
  propertyType: string;
  minBidEok: number;
  appraisedEok: number;
  discountPct: number;
  dday: number;
  auctionDate: string;
  marketEstEok: number | null;
  marketBasis: string | null;
  dealCount: number | null;
  marginPct: number | null;
  priceWarn: boolean;
  riskConfirmed: boolean;
  riskCount: number;
  areaM2: number | null;
  hasPhoto: boolean;
}

interface SearchCasesData {
  total: number;
  page: number;
  pageSize: number;
  capped: boolean;
  items: PublicCaseCard[];
}

export interface CaseSearchFilters {
  region?: string;
  type?: string;
}

// 조건 하나만 좁혀도 수백 건이 나올 수 있다(예: 서울×다세대 500건) — 다 받아오는
// 건 UI로도 의미 없고, 60건 사진/시세 보강 호출과 겹치면 레이트리밋(429)에 걸린다.
// 첫 페이지만 보여주고 나머지는 total로 안내한다.
const PAGE_SIZE = 24;

export function useCaseSearch(filters: CaseSearchFilters, enabled: boolean) {
  const [items, setItems] = useState<PublicCaseCard[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setTotal(0);
      setError(null);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = (await echotwinApi.searchCases({
          ...filters,
          page: 1,
          pageSize: PAGE_SIZE,
        })) as { data: SearchCasesData };
        if (cancelled) return;
        setItems(res.data.items);
        setTotal(res.data.total);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "검색에 실패했습니다.");
        setItems([]);
        setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
    // filters는 매 렌더 새 객체라 region/type 값만 의존성으로 본다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filters.region, filters.type]);

  return { items, total, loading, error };
}
